import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  deleteAllLocalSyncRepos,
  deleteLocalSyncRepo,
  syncLocalSyncRepos
} from '~/services/localSyncService'
import { RootState } from '~/store'
import { FileHandleStore } from '~/utils/FileHandleStore'
import { cronScheduler } from '~/utils/cronScheduler'
import { FolderHasher } from '~/utils/folder-hasher'


interface SerializableHandle {
  id: string
  lastFolderHash?: string
  lastSyncedAt?: string // Timestamp of last successful sync
  name?: string // Store the handle name for easier reference
  securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING' // Track security status
}


interface UserDirHandleState {
  handles: SerializableHandle[]
  isInitialized: boolean
  permissionStatus: 'approved' | 'rejected' | 'prompt'
  error: string | null
  syncStatus?: 'syncing' | 'synced' | 'error' // Sync status
  lastSyncAt?: string // Timestamp of last sync attempt
}

interface DirHandleState {
  _currentUserEmail: string | null
  userStates: Record<string, UserDirHandleState>
  version: number
}

// This is used to check if the user state is up to date
export const CURRENT_DIRHANDLE_VERSION = 3

const createInitialUserState = (): UserDirHandleState => ({
  handles: [],
  isInitialized: false,
  error: null,
  permissionStatus: 'approved',
  syncStatus: undefined,
  lastSyncAt: undefined
})

const defaultState: DirHandleState = {
  _currentUserEmail: null,
  userStates: {},
  version: CURRENT_DIRHANDLE_VERSION
}

const fileHandleStore = new FileHandleStore()

export const checkPermission = async (dirHandle: FileSystemDirectoryHandle) => {
  const currentPermission = await (dirHandle as any).queryPermission({ mode: 'readwrite' })
  return currentPermission === 'granted'
}

export const requestPermission = async (dirHandle: FileSystemDirectoryHandle) => {
  const permission = await (dirHandle as any).requestPermission({ mode: 'readwrite' })
  return permission === 'granted'
}

export const initializeUserAndHandles = createAsyncThunk(
  'dirHandle/initialize',
  async (userEmail: string, { getState, dispatch }) => {
    // Initialize fileHandleStore
    await fileHandleStore.init()

    // Get user's existing handles from state
    const state = getState() as RootState
    const userState = state.dirHandle.userStates[userEmail] || createInitialUserState()
    const existingHandleIds = userState.handles.map(h => h.id)

    // Check permissions and verify handles
    const verifiedHandles: SerializableHandle[] = []
    let hasPrompt = false
    let hasRejected = false

    for (const handleId of existingHandleIds) {
      try {
        const storedHandle = await fileHandleStore.getHandleById(handleId)
        if (storedHandle) {
          const permission = await (storedHandle.handle as any).queryPermission({
            mode: 'readwrite'
          })

          // Store handle with name for easier reference
          const existingHandle = userState.handles.find(h => h.id === handleId)

          if (permission === 'granted') {
            verifiedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: storedHandle.handle.name
            })
          } else if (permission === 'prompt') {
            hasPrompt = true
            verifiedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: storedHandle.handle.name
            })
          } else if (permission === 'denied') {
            hasRejected = true
          }
        }
      } catch (error) {
        console.warn(`Failed to verify handle ${handleId}:`, error)
        hasRejected = true
      }
    }

    // Determine permission status
    let permissionStatus: 'approved' | 'rejected' | 'prompt'
    if (hasRejected) {
      permissionStatus = 'rejected'
    } else if (hasPrompt) {
      permissionStatus = 'prompt'
    } else {
      permissionStatus = 'approved'
    }

    // Version check
    const currentVersion = state.dirHandle.version ?? 0
    const shouldReset = !currentVersion || currentVersion < CURRENT_DIRHANDLE_VERSION

    // Get list of valid handles by name for S3 sync
    const verifiedHandleNames = verifiedHandles.map(handle => handle.name).filter(Boolean) as string[]

    // We only want to delete repositories if we've properly verified our dirHandles
    // and there are no handles available (this indicates a clean state)
    if (verifiedHandles.length === 0) {
      // Remove all uploads from S3 when there are no valid handles
      try {
        await deleteAllLocalSyncRepos()
      } catch (error) {
        console.error('Failed to clean up device repositories during initialization:', error)
      }
    }

    // We'll trigger a sync after initialization is complete
    if (permissionStatus === 'approved' && verifiedHandles.length > 0) {
      setTimeout(() => {
        dispatch(syncDirHandlesWithBucket())
      }, 1000) // Delay sync slightly after initialization
    }

    return {
      userEmail,
      handles: verifiedHandles,
      permissionStatus,
      shouldReset,
      validHandleNames: verifiedHandleNames
    }
  }
)

export const syncDirHandlesWithBucket = createAsyncThunk(
  'dirHandle/syncWithBucket',
  async (_, { getState }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    const userState = state.dirHandle.userStates[userEmail]
    if (!userState?.isInitialized) throw new Error('User state not initialized')

    // Get all valid handles with names
    const validHandles = await Promise.all(
      userState.handles.map(async handle => {
        try {
          const storedHandle = await fileHandleStore.getHandleById(handle.id)
          if (storedHandle) {
            const permission = await (storedHandle.handle as any).queryPermission({
              mode: 'readwrite'
            })
            if (permission === 'granted' || permission === 'prompt') {
              return {
                id: handle.id,
                name: storedHandle.handle.name,
                lastFolderHash: handle.lastFolderHash,
                lastSyncedAt: handle.lastSyncedAt
              }
            }
          }
        } catch (error) {
          console.warn(`Failed to verify handle ${handle.id} during sync:`, error)
        }
        return null
      })
    )

    const validHandleList = validHandles.filter(Boolean) as SerializableHandle[]
    const validHandleNames = validHandleList.map(h => h.name).filter(Boolean) as string[]

    if (validHandleNames.length === 0 && userState.handles.length > 0) {
      console.warn('No valid handles with permissions found during sync')
    }

    // Call the API to sync with the backend
    // Only send the list of valid handle names we want to keep
    if (validHandleNames.length > 0) {
      try {
        const deviceId = window.getDeviceId()
        await syncLocalSyncRepos(deviceId, validHandleNames)
        console.info(`Synchronized ${validHandleNames.length} repositories with the server`)
      } catch (error) {
        console.error('Failed to sync repositories with the server:', error)
        // We continue even if this fails, to at least update the local state
      }
    } else {
      // If no valid handles, clear everything
      try {
        const deviceId = window.getDeviceId()
        await syncLocalSyncRepos(deviceId, [])
        console.info('Cleared all repositories from the server')
      } catch (error) {
        console.error('Failed to clear repositories from the server:', error)
      }
    }

    // Mark all valid handles as synced
    const now = new Date().toISOString()
    return {
      syncedHandles: validHandleList.map(h => ({
        ...h,
        lastSyncedAt: now
      })),
      lastSyncAt: now
    }
  }
)

export const requestAllPermissions = createAsyncThunk(
  'dirHandle/requestPermissions',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    const userState = state.dirHandle.userStates[userEmail]
    const existingHandleIds = userState.handles.map(h => h.id)

    const updatedHandles: SerializableHandle[] = []
    let hasRejected = false
    let hasPrompt = false

    for (const handleId of existingHandleIds) {
      try {
        const stored = await fileHandleStore.getHandleById(handleId)
        if (stored) {
          const currentPermission = await (stored.handle as any).queryPermission({
            mode: 'readwrite'
          })

          // Find existing handle to preserve properties
          const existingHandle = userState.handles.find(h => h.id === handleId)

          if (currentPermission === 'prompt') {
            const granted = await requestPermission(stored.handle)
            if (granted) {
              updatedHandles.push({
                id: handleId,
                lastFolderHash: existingHandle?.lastFolderHash,
                lastSyncedAt: existingHandle?.lastSyncedAt,
                name: stored.handle.name
              })
            } else {
              hasPrompt = true
            }
          } else if (currentPermission === 'granted') {
            updatedHandles.push({
              id: handleId,
              lastFolderHash: existingHandle?.lastFolderHash,
              lastSyncedAt: existingHandle?.lastSyncedAt,
              name: stored.handle.name
            })
          } else if (currentPermission === 'denied') {
            hasRejected = true
          }
        }
      } catch (error) {
        console.warn(`Failed to check permissions for handle ${handleId}:`, error)
        hasRejected = true
      }
    }

    // Determine new permission status
    let permissionStatus: 'approved' | 'rejected' | 'prompt'
    if (hasRejected) {
      permissionStatus = 'rejected'
    } else if (hasPrompt) {
      permissionStatus = 'prompt'
    } else {
      permissionStatus = 'approved'
    }

    // After updating permissions, trigger a sync with bucket
    if (updatedHandles.length > 0 && permissionStatus === 'approved') {
      dispatch(syncDirHandlesWithBucket())
    }

    return {
      handles: updatedHandles,
      permissionStatus
    }
  }
)

// Define a type for addDirHandle return value
interface AddDirHandleResult {
  id: string;
  name: string;
  lastFolderHash?: string;
  securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING';
}

export const addDirHandle = createAsyncThunk<AddDirHandleResult, FileSystemDirectoryHandle>(
  'dirHandle/add',
  async (dirHandle: FileSystemDirectoryHandle, { getState, dispatch }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    const hasPermission = await checkPermission(dirHandle)
    if (!hasPermission) {
      const granted = await requestPermission(dirHandle)
      if (!granted) {
        throw new Error('Permission denied for directory')
      }
    }

    const id = await fileHandleStore.saveHandle(dirHandle)

    // Calculate the hash but don't wait for processing to complete
    // This lets us return quickly and update the UI
    try {
      const folderHasher = new FolderHasher()
      const folderHash = await folderHasher.getFolderHash(dirHandle)

      // Return immediately with the hash so UI can update
      const result: AddDirHandleResult = {
        id,
        name: dirHandle.name,
        lastFolderHash: folderHash,
        securityStatus: 'SCANNING' // Mark as scanning initially
      }

      // Process in the background without blocking
      setTimeout(async () => {
        try {
          // Upload the files
          await cronScheduler.processDirectory(dirHandle, id, true)

          // Sync with the bucket after upload
          dispatch(syncDirHandlesWithBucket())
        } catch (error) {
          console.error('Failed to process directory in background:', error)

          // Check for virus detection in error message
          if (error instanceof Error &&
            error.message.toLowerCase().includes('virus detected')) {
            dispatch(updateSecurityStatus({ id, securityStatus: 'THREATS_FOUND' }))
          }
        }
      }, 0)

      return result
    } catch (error) {
      console.error('Failed to calculate directory hash:', error)
      // Return basic info if hash calculation fails
      return {
        id,
        name: dirHandle.name
      }
    }
  }
)

export const removeDirHandle = createAsyncThunk(
  'dirHandle/remove',
  async (id: string, { getState, dispatch }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    // Get the handle name before removing
    const userState = state.dirHandle.userStates[userEmail]
    const handleToRemove = userState.handles.find(h => h.id === id)
    const handleName = handleToRemove?.name

    await fileHandleStore.removeHandle(id)

    // After removing a handle, remove corresponding repo
    if (handleName) {
      try {
        await deleteLocalSyncRepo(handleName)
        console.info(`Removed repository for ${handleName}`)
      } catch (error) {
        console.error(`Failed to delete device repo for ${handleName}:`, error)
      }
    }

    // Then sync to make sure everything is consistent
    dispatch(syncDirHandlesWithBucket())

    return { id }
  }
)

export const updateSecurityStatus = createAsyncThunk(
  'dirHandle/updateSecurityStatus',
  async ({ id, securityStatus }: { id: string; securityStatus: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING' }, { getState }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    return { id, securityStatus }
  }
)

export const updateFolderHash = createAsyncThunk(
  'dirHandle/updateFolderHash',
  async ({ id, folderHash }: { id: string; folderHash: string }, { getState }) => {
    const state = getState() as RootState
    const userEmail = state.dirHandle._currentUserEmail
    if (!userEmail) throw new Error('No user initialized')

    const now = new Date().toISOString()
    return { id, folderHash, lastSyncedAt: now }
  }
)

// New action for manually triggering directory synchronization
export const manualSyncDirectories = createAsyncThunk(
  'dirHandle/manualSync',
  async (_, { dispatch }) => {
    await dispatch(syncDirHandlesWithBucket()).unwrap()
    return true
  }
)

export const dirHandleSlice = createSlice({
  name: 'dirHandle',
  initialState: defaultState,
  reducers: {
    cleanupUserState: (state, action) => {
      const userId = action.payload
      delete state.userStates[userId]
      if (state._currentUserEmail === userId) {
        state._currentUserEmail = null
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(initializeUserAndHandles.pending, (state, action) => {
        const userEmail = action.meta.arg

        // Version check logging
        console.info(
          `[DirHandle] Version check - Current: ${state.version ?? 'none'} => Required: ${CURRENT_DIRHANDLE_VERSION}${!state.version || state.version < CURRENT_DIRHANDLE_VERSION
            ? ' (Resetting dirHandle state)'
            : ' (Up to date)'
          }`
        )

        if (state.userStates == null) {
          state.userStates = {}
        }
        state._currentUserEmail = userEmail
        if (!state.userStates[userEmail]) {
          state.userStates[userEmail] = createInitialUserState()
        }
        state.userStates[userEmail].isInitialized = false
        state.userStates[userEmail].error = null
      })
      .addCase(initializeUserAndHandles.fulfilled, (state, action) => {
        const { userEmail, handles, shouldReset, permissionStatus } = action.payload

        // Handle version migration
        if (shouldReset) {
          state.userStates = {}
          state.version = CURRENT_DIRHANDLE_VERSION
          state.userStates[userEmail] = createInitialUserState()
        }

        const userState = state.userStates[userEmail]
        userState.permissionStatus = permissionStatus

        const existingIds = new Set(userState.handles.map(h => h.id))
        const newIds = new Set(handles.map(h => h.id))

        userState.handles = userState.handles.filter(handle => newIds.has(handle.id))
        const handlesToAdd = handles.filter(handle => !existingIds.has(handle.id))
        userState.handles.push(...handlesToAdd)

        // Update existing handles with new properties
        handles.forEach(newHandle => {
          const existingHandle = userState.handles.find(h => h.id === newHandle.id)
          if (existingHandle && newHandle.name) {
            existingHandle.name = newHandle.name
          }
        })

        userState.isInitialized = true
        userState.error = null
        userState.syncStatus = 'synced'
        userState.lastSyncAt = new Date().toISOString()
      })
      .addCase(initializeUserAndHandles.rejected, (state, action) => {
        const userEmail = action.meta.arg
        const userState = state.userStates[userEmail]
        if (userState) {
          userState.error = action.error.message || 'Failed to initialize user and handles'
          userState.isInitialized = false
          userState.syncStatus = 'error'
        }
      })
      .addCase(syncDirHandlesWithBucket.pending, (state) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.syncStatus = 'syncing'
      })
      .addCase(syncDirHandlesWithBucket.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]

        const { syncedHandles, lastSyncAt } = action.payload

        // Update each handle with sync info
        syncedHandles.forEach(syncedHandle => {
          const existingHandle = userState.handles.find(h => h.id === syncedHandle.id)
          if (existingHandle) {
            existingHandle.lastSyncedAt = syncedHandle.lastSyncedAt
          }
        })

        userState.syncStatus = 'synced'
        userState.lastSyncAt = lastSyncAt
        userState.error = null
      })
      .addCase(syncDirHandlesWithBucket.rejected, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.syncStatus = 'error'
        userState.error = action.error.message || 'Failed to sync with bucket'
      })
      .addCase(manualSyncDirectories.pending, (state) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.syncStatus = 'syncing'
      })
      .addCase(manualSyncDirectories.fulfilled, (state) => {
        // The syncDirHandlesWithBucket action will update the state
      })
      .addCase(manualSyncDirectories.rejected, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.syncStatus = 'error'
        userState.error = action.error.message || 'Failed to manually sync directories'
      })
      .addCase(addDirHandle.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]

        const newHandle = action.payload
        if (newHandle.id) {
          const existingHandleWithHash = userState.handles.find(
            handle => handle.id === newHandle.id
          )

          if (existingHandleWithHash) {
            return
          }
        }

        // Add handle to state first so UI is updated
        userState.handles.push({
          id: newHandle.id,
          name: newHandle.name,
          lastFolderHash: newHandle.lastFolderHash, // Include the hash in the state
          securityStatus: newHandle.securityStatus // Include security status
        })
        userState.error = null
      })
      .addCase(addDirHandle.rejected, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.error = action.error.message || 'Failed to add directory handle'
      })
      .addCase(removeDirHandle.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.handles = userState.handles.filter(handle => handle.id !== action.payload.id)
        userState.error = null
      })
      .addCase(requestAllPermissions.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]

        const { handles, permissionStatus } = action.payload
        const existingIds = new Set(userState.handles.map(h => h.id))
        const newIds = new Set(handles.map(h => h.id))

        userState.handles = userState.handles.filter(handle => newIds.has(handle.id))
        const handlesToAdd = handles.filter(handle => !existingIds.has(handle.id))
        userState.handles.push(...handlesToAdd)

        // Update existing handles with names
        handles.forEach(newHandle => {
          const existingHandle = userState.handles.find(h => h.id === newHandle.id)
          if (existingHandle && newHandle.name) {
            existingHandle.name = newHandle.name
          }
        })

        userState.permissionStatus = permissionStatus
        userState.error = null
      })
      .addCase(requestAllPermissions.rejected, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        userState.error = action.error.message || 'Failed to request permissions'
      })
      .addCase(updateFolderHash.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        const handle = userState.handles.find(h => h.id === action.payload.id)
        if (handle) {
          handle.lastFolderHash = action.payload.folderHash
          handle.lastSyncedAt = action.payload.lastSyncedAt
        }
      })
      .addCase(updateSecurityStatus.fulfilled, (state, action) => {
        if (!state._currentUserEmail) return
        const userState = state.userStates[state._currentUserEmail]
        const handle = userState.handles.find(h => h.id === action.payload.id)
        if (handle) {
          handle.securityStatus = action.payload.securityStatus
        }
      })
  }
})

// Selectors
export const selectCurrentUserEmail = (state: RootState): string | null => {
  return state.dirHandle._currentUserEmail
}

export const selectUserDirHandleState = (
  state: RootState,
  userEmail?: string
): UserDirHandleState => {
  const email = userEmail ?? state.dirHandle._currentUserEmail
  if (!email) {
    return {
      handles: [],
      isInitialized: false,
      permissionStatus: 'approved',
      error: null
    }
  }
  return (
    state.dirHandle.userStates[email] || {
      handles: [],
      isInitialized: false,
      error: null,
      permissionStatus: 'approved'
    }
  )
}

export const selectUserHandles = (state: RootState, userEmail?: string): SerializableHandle[] => {
  const userState = selectUserDirHandleState(state, userEmail)
  return userState.handles
}

export const selectUserHandleById = (
  state: RootState,
  handleId: string,
  userEmail?: string
): SerializableHandle | undefined => {
  const handles = selectUserHandles(state, userEmail)
  return handles.find(handle => handle.id === handleId)
}

export const selectUserInitializationStatus = (
  state: RootState,
  userEmail?: string
): { isInitialized: boolean; error: string | null; syncStatus?: string } => {
  const userState = selectUserDirHandleState(state, userEmail)
  return {
    isInitialized: userState.isInitialized,
    error: userState.error,
    syncStatus: userState.syncStatus
  }
}

export const selectHandleFolderHash = (
  state: RootState,
  handleId: string,
  userEmail?: string
): string | undefined => {
  const handle = selectUserHandleById(state, handleId, userEmail)
  return handle?.lastFolderHash
}

export const selectAllHandlesWithHashes = (
  state: RootState,
  userEmail?: string
): Array<{ id: string; lastFolderHash?: string; name?: string; securityStatus?: 'THREATS_FOUND' | 'CLEAN' | 'SCANNING' }> => {
  return selectUserHandles(state, userEmail).map(handle => ({
    id: handle.id,
    lastFolderHash: handle.lastFolderHash,
    name: handle.name,
    securityStatus: handle.securityStatus
  }))
}

export const { cleanupUserState } = dirHandleSlice.actions

export { defaultState as dirHandleDefaultState }
export default dirHandleSlice.reducer
